// PointsConfiguration.cshtml: the platform default rate, per-program overrides (with an expiry), CSV export.
import { dateTimeUtc, shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { adminApi, type PointsRates } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { confirmed, ResultBanner } from '../components/ui';
import { download, expiringSoonCount, highestBonus, rateFormula, ratePreview, ratesCsv } from '../points';

export function PointsConfigurationPage() {
  usePageTitle('Points Configuration');
  const client = useQueryClient();
  const rates = useQuery({ queryKey: ['points-rates'], queryFn: adminApi.pointsRates });
  // the last change of the default (QOR staff only: the audit log; nothing shown otherwise)
  const lastChange = useQuery({
    queryKey: ['points-rates', 'last-change'],
    queryFn: async () =>
      (await adminApi.auditLog({ action: 'DefaultPointsRateChanged', page: 1, pageSize: 1 })).items[0] ?? null,
  });
  const [outcome, setOutcome] = useState<{ message: string | null; ok: boolean }>({ message: null, ok: false });

  async function after(ok: boolean, message: string) {
    setOutcome({ ok, message });
    await client.invalidateQueries({ queryKey: ['points-rates'] });
  }

  const data = rates.data;
  return (
    <>
      <div className="qc-page-heading-row">
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            Points Configuration <span className="qc-badge qc-badge-accent">Live Rate Engine</span>
          </h1>
          <p className="qc-scope-label" style={{ marginTop: 6 }}>
            Manage default conversion ratios, program-specific overrides &amp; expiration rules
          </p>
        </div>
        {data && (
          <button
            type="button"
            className="qc-button-ghost"
            onClick={() => download('qor-points-rates.csv', ratesCsv(data))}
          >
            Export Rates
          </button>
        )}
      </div>

      <ResultBanner {...outcome} />

      {rates.isPending ? null : !data ? (
        <p className="qc-empty">Could not load points rate configuration.</p>
      ) : (
        <RatesView
          key={rates.dataUpdatedAt}
          rates={data}
          lastChange={lastChange.data ?? null}
          after={(ok, message) => void after(ok, message)}
        />
      )}
    </>
  );
}

function RatesView({
  rates,
  lastChange,
  after,
}: {
  rates: PointsRates;
  lastChange: { actorDisplayName: string; actorEmail: string; occurredAt: string } | null;
  after: (ok: boolean, message: string) => void;
}) {
  const [def, setDef] = useState({ points: String(rates.default.pointsPerUnit), currency: rates.default.currency });
  const emptyOverride = { programId: '', points: '', currency: 'USD', expiresOn: '' };
  const [ovr, setOvr] = useState(emptyOverride);
  const [busy, setBusy] = useState(false);
  const unit = rates.default.pointsPerUnit > 0 ? 1 / rates.default.pointsPerUnit : 0;
  const expiring = expiringSoonCount(rates.programOverrides, new Date());
  const bonus = highestBonus(rates);
  const today = new Date().toISOString().slice(0, 10);

  async function saveDefault(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await adminApi.setDefaultRate(Number.parseInt(def.points, 10) || 0, def.currency);
    setBusy(false);
    after(r.success, r.success ? 'Default points rate saved.' : (r.error ?? 'Could not save the default rate.'));
  }

  async function saveOverride(e: FormEvent) {
    e.preventDefault();
    if (ovr.programId === '') return after(false, 'Choose a program.');
    setBusy(true);
    const r = await adminApi.setProgramRate(
      ovr.programId,
      Number.parseInt(ovr.points, 10) || 0,
      ovr.currency,
      ovr.expiresOn === '' ? null : ovr.expiresOn,
    );
    setBusy(false);
    after(r.success, r.success ? 'Program override saved.' : (r.error ?? 'Could not save the program override.'));
  }

  async function remove(programId: string | null) {
    if (!programId || !confirmed('Revert this program to the default rate?')) return;
    const ok = await adminApi.removeProgramRate(programId);
    after(ok, ok ? 'Program reverted to the default rate.' : 'Could not remove the override.');
  }

  const defPoints = Number.parseFloat(def.points) || 0;
  const ovrPoints = Number.parseFloat(ovr.points) || 0;

  return (
    <>
      <section className="qc-stat-row" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3 style={{ fontSize: '.95rem' }}>Platform Standard Ratio</h3>
            <span className="qc-badge qc-badge-accent">Default</span>
          </div>
          <p style={{ fontSize: '1.6rem', fontWeight: 900, margin: '0 0 6px' }}>
            {rates.default.pointsPerUnit} pts <span style={{ color: 'var(--qor-muted)', fontWeight: 700 }}>=</span> 1.00{' '}
            {rates.default.currency}
          </p>
          <p style={{ color: 'var(--qor-muted)', fontSize: '.85rem', margin: '0 0 14px' }}>
            Effective unit value: {unit.toFixed(6)} {rates.default.currency} / pt
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '.8rem',
              color: 'var(--qor-muted)',
              borderTop: '1px solid var(--qor-line)',
              paddingTop: 12,
            }}
          >
            <span>Scope: Global Fallback</span>
            <span className="qc-badge qc-badge-success">Active</span>
          </div>
        </div>

        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3 style={{ fontSize: '.95rem' }}>Active Overrides</h3>
            <span className="qc-badge qc-badge-purple">Scoped</span>
          </div>
          <p style={{ fontSize: '1.6rem', fontWeight: 900, margin: '0 0 6px' }}>
            {rates.programOverrides.length} Programs
          </p>
          <p style={{ color: 'var(--qor-muted)', fontSize: '.85rem', margin: '0 0 14px' }}>
            {expiring > 0
              ? `${expiring} tier${expiring === 1 ? '' : 's'} expiring within 30 days`
              : 'No tiers expiring soon'}
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '.8rem',
              color: 'var(--qor-muted)',
              borderTop: '1px solid var(--qor-line)',
              paddingTop: 12,
            }}
          >
            {bonus ? (
              <span>
                Highest bonus:{' '}
                <strong style={{ color: 'var(--qor-success)' }}>+{bonus.bonusPercent.toFixed(1)}%</strong> (
                {bonus.programName})
              </span>
            ) : (
              <span>No bonus overrides</span>
            )}
          </div>
        </div>
      </section>

      <div className="qc-detail-row">
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3>
              Platform Default{' '}
              <span className="qc-badge qc-badge-muted" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                Standard Rule
              </span>
            </h3>
            <span>Applies to every program without its own override</span>
          </div>
          <form
            onSubmit={(e) => void saveDefault(e)}
            onReset={() => setDef({ points: String(rates.default.pointsPerUnit), currency: rates.default.currency })}
            className="qc-points-rate-form"
          >
            <label className="qc-field">
              <span>Points per unit of currency</span>
              <input
                type="number"
                id="qc-default-points"
                min="1"
                step="1"
                required
                value={def.points}
                onChange={(e) => setDef({ ...def, points: e.target.value })}
              />
              <small style={{ color: 'var(--qor-muted)', fontSize: '.78rem' }}>
                Total loyalty points required to equal exactly 1.00 unit of base currency.
              </small>
            </label>
            <label className="qc-field">
              <span>Currency</span>
              <select
                id="qc-default-currency"
                value={def.currency}
                onChange={(e) => setDef({ ...def, currency: e.target.value })}
              >
                <option value="USD">USD -- United States Dollar ($)</option>
                <option value="CAD">CAD -- Canadian Dollar ($)</option>
              </select>
            </label>
            <div className="qc-points-rate-preview-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
                <span>Conversion Preview:</span>
                <span id="qc-default-preview">{ratePreview(defPoints, def.currency)}</span>
              </div>
              <code className="qc-code-chip" id="qc-default-formula" style={{ display: 'block', marginTop: 8 }}>
                {rateFormula(defPoints, def.currency)}
              </code>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                Save Default
              </button>
              <button type="reset" className="qc-button-ghost">
                Reset
              </button>
            </div>
            {lastChange && (
              <p style={{ color: 'var(--qor-muted)', fontSize: '.78rem', margin: '14px 0 0' }}>
                Last saved by <strong>{lastChange.actorDisplayName}</strong> ({lastChange.actorEmail}) on{' '}
                {dateTimeUtc(lastChange.occurredAt)?.replace(/ (\d+:\d\d [AP]M)$/, ', $1')} UTC
              </p>
            )}
          </form>
        </div>

        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3>
              Add or Update a Program Override{' '}
              <span className="qc-badge qc-badge-purple" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                Scoped Rule
              </span>
            </h3>
            <span>Overrides the platform default for one designated program</span>
          </div>
          <form
            onSubmit={(e) => void saveOverride(e)}
            onReset={() => setOvr(emptyOverride)}
            className="qc-points-rate-form"
          >
            <label className="qc-field">
              <span>Program</span>
              <select value={ovr.programId} onChange={(e) => setOvr({ ...ovr, programId: e.target.value })}>
                <option value="">Choose a program...</option>
                {rates.availablePrograms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <label className="qc-field">
                <span>Points per unit of currency</span>
                <input
                  type="number"
                  id="qc-override-points"
                  min="1"
                  step="1"
                  required
                  value={ovr.points}
                  onChange={(e) => setOvr({ ...ovr, points: e.target.value })}
                />
              </label>
              <label className="qc-field">
                <span>Currency</span>
                <select
                  id="qc-override-currency"
                  value={ovr.currency}
                  onChange={(e) => setOvr({ ...ovr, currency: e.target.value })}
                >
                  <option value="USD">USD ($)</option>
                  <option value="CAD">CAD ($)</option>
                </select>
              </label>
            </div>
            <label className="qc-field">
              <span>Expires (optional)</span>
              <input
                type="date"
                min={today}
                value={ovr.expiresOn}
                onChange={(e) => setOvr({ ...ovr, expiresOn: e.target.value })}
              />
              <small style={{ color: 'var(--qor-muted)', fontSize: '.78rem' }}>
                Leave blank for no expiry. After the date passes, this program automatically reverts to the platform
                default.
              </small>
            </label>
            <div className="qc-points-rate-preview-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
                <span>Ratio Calculation Preview:</span>
                <span id="qc-override-preview">
                  {ratePreview(ovrPoints, ovr.currency, rates.default.pointsPerUnit)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                + Save Override
              </button>
              <button type="reset" className="qc-button-ghost">
                Clear Form
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="qc-panel" style={{ marginTop: 24 }}>
        <div className="qc-panel-title">
          <h3>Program Overrides</h3>
          <span className="qc-badge qc-badge-accent">{rates.programOverrides.length} Configured</span>
        </div>
        {rates.programOverrides.length === 0 ? (
          <p className="qc-empty">No program is overriding the default rate yet.</p>
        ) : (
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Rate</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rates.programOverrides.map((r) => (
                <tr key={r.programId ?? r.programName}>
                  <td>
                    <span className="qc-row-dot" aria-hidden="true"></span>
                    {r.programName}
                  </td>
                  <td>
                    {r.pointsPerUnit} points = 1.00 {r.currency}
                  </td>
                  <td>{shortDate(r.expiresOn) ?? 'No expiry'}</td>
                  <td>
                    <button type="button" className="qc-button-ghost" onClick={() => void remove(r.programId)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
